import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { ActionCardData, AgentChatMessage } from '@/lib/chatTypes'
import { Skeleton } from '@/components/ui/skeleton'
import { AgentMessage } from '@/components/agents/AgentMessage'
import { UserMessage } from '@/components/agents/UserMessage'

// Au-delà de cette distance au bas, l'utilisateur a remonté : pas d'auto-scroll.
const STICK_TO_BOTTOM_THRESHOLD_PX = 80

export type MessageListProps = {
  messages: AgentChatMessage[]
  welcome?: ReactNode
  loading?: boolean
  error?: string | null
  onActionPrimary?: (messageId: string, card: ActionCardData) => void
  onActionSecondary?: (messageId: string, card: ActionCardData) => void
  // CTAs des ActionCards désactivés (pendant un stream : un seul envoi à la fois).
  actionsDisabled?: boolean
  // Story 7.1 (PO Q3) — lien d'une carte exécutée.
  onNavigate?: (route: string) => void
  className?: string
}

export function MessageList({
  messages,
  welcome,
  loading,
  error,
  onActionPrimary,
  onActionSecondary,
  actionsDisabled,
  onNavigate,
  className,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const stickRef = useRef(true)

  const handleScroll = () => {
    const el = containerRef.current
    if (!el) return
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_TO_BOTTOM_THRESHOLD_PX
  }

  const prevCountRef = useRef(messages.length)
  const prevLoadingRef = useRef(loading)

  // Fin d'un chargement d'historique (changement de conversation) : on
  // repart collé en bas, quel que soit le scroll de la conversation précédente.
  useEffect(() => {
    if (prevLoadingRef.current && !loading) stickRef.current = true
    prevLoadingRef.current = loading
  }, [loading])

  const lastMessage = messages[messages.length - 1]
  const isStreamingLast = lastMessage?.role === 'assistant' && lastMessage.status === 'streaming'

  // Auto-scroll à chaque nouveau message / chunk, sauf si l'utilisateur a
  // remonté. Un nouvel envoi de l'utilisateur ramène toujours en bas.
  useEffect(() => {
    const el = containerRef.current
    const added = messages.length > prevCountRef.current
    prevCountRef.current = messages.length
    if (!el) return
    const userJustSent = added && messages.some((m, i) => i >= messages.length - 2 && m.role === 'user')
    if (userJustSent) stickRef.current = true
    if (!stickRef.current) return
    el.scrollTop = el.scrollHeight
  }, [messages])

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      role="log"
      aria-live="polite"
      // Pendant le stream, les lecteurs d'écran attendent la fin de la réponse
      // au lieu d'annoncer chaque chunk.
      aria-busy={isStreamingLast}
      aria-label="Messages de la conversation"
      className={cn('flex-1 min-h-0 overflow-y-auto', className)}
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 py-6">
        {loading ? (
          <div className="space-y-4" aria-busy="true">
            <Skeleton className="ml-auto h-10 w-1/2" />
            <Skeleton className="h-20 w-3/4" />
            <Skeleton className="ml-auto h-10 w-1/3" />
          </div>
        ) : error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        ) : (
          <>
            {messages.length === 0 ? welcome : null}
            {messages.map((m) =>
              m.role === 'user' ? (
                <UserMessage
                  key={m.id}
                  content={m.content}
                  createdAt={m.createdAt}
                  state={m.status === 'sending' ? 'sending' : m.status === 'error' ? 'error' : 'sent'}
                />
              ) : (
                <AgentMessage
                  key={m.id}
                  message={m}
                  onActionPrimary={onActionPrimary ? (card) => onActionPrimary(m.id, card) : undefined}
                  onActionSecondary={onActionSecondary ? (card) => onActionSecondary(m.id, card) : undefined}
                  actionsDisabled={actionsDisabled}
                  onNavigate={onNavigate}
                />
              )
            )}
          </>
        )}
      </div>
    </div>
  )
}
